import { useState, useRef } from 'react';
import { Icon } from '@iconify/react';

const TEMPLATE_URL = '/assets/samples/journal_voucher_sample.xlsx';
const ALLOWED_EXTENSIONS = ['csv', 'xlsx', 'xls'];

const BulkImportUpload = ({ file: existingFile, onNext, onCancel, loading }) => {
    const [file, setFile] = useState(existingFile);
    const [fileError, setFileError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const handleFile = (selectedFile) => {
        if (!selectedFile) return;

        const ext = selectedFile.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            setFileError('Only CSV and XLSX files are supported.');
            setFile(null);
            return;
        }

        setFileError(null);
        setFile(selectedFile);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFile(e.dataTransfer.files[0]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleInputChange = (e) => {
        handleFile(e.target.files[0]);
    };

    const handleDownloadTemplate = () => {
        const a = document.createElement('a');
        a.href = TEMPLATE_URL;
        a.download = 'journal_voucher_sample.xlsx';
        a.click();
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFileError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const formatSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <div>
            {/* Main Card */}
            <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                <div className="tw-grid tw-grid-cols-2">
                    {/* Left Side - Hero Text */}
                    <div className="tw-p-10 tw-flex tw-flex-col tw-justify-center tw-border-r tw-border-gray-100">
                        <p className="tw-text-sm tw-text-gray-400 tw-font-medium">Seamless</p>
                        <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mt-0.5">Journal Import</h2>
                        <p className="tw-text-xs tw-text-gray-400 tw-mt-3 tw-leading-relaxed">
                            Experience precision data management. Drop your CSV records to initiate our intelligent pre-flight processing engine.
                        </p>
                        <ul className="tw-mt-5 tw-space-y-3">
                            <li className="tw-flex tw-items-center tw-gap-2">
                                <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500" />
                                <span className="tw-text-sm tw-font-medium tw-text-gray-700">Auto-field mapping</span>
                            </li>
                            <li className="tw-flex tw-items-center tw-gap-2">
                                <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500" />
                                <span className="tw-text-sm tw-font-medium tw-text-gray-700">Instant duplicate detection</span>
                            </li>
                            <li className="tw-flex tw-items-center tw-gap-2">
                                <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500" />
                                <span className="tw-text-sm tw-font-medium tw-text-gray-700">Balance validation</span>
                            </li>
                        </ul>
                    </div>

                    {/* Right Side - Upload Area */}
                    <div className="tw-p-10 tw-flex tw-flex-col tw-items-center tw-justify-center">
                        {/* Drop Zone */}
                        <div
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClick={() => fileInputRef.current?.click()}
                            className={`tw-w-full tw-border-2 tw-border-dashed tw-rounded-xl tw-py-12 tw-px-8 tw-text-center tw-cursor-pointer tw-transition-colors ${
                                isDragging
                                    ? 'tw-border-indigo-400 tw-bg-indigo-50'
                                    : 'tw-border-gray-200 hover:tw-border-gray-300 tw-bg-gray-50/50'
                            }`}
                        >
                            <div className="tw-w-14 tw-h-14 tw-rounded-full tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                                <Icon icon="mdi:cloud-upload-outline" className="tw-text-2xl tw-text-indigo-400" />
                            </div>
                            <p className="tw-text-sm tw-font-semibold tw-text-gray-700">Drag files to import</p>
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-1">
                                or <span className="tw-text-blue-500 tw-font-medium hover:tw-underline">browse local files</span>
                            </p>
                            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mt-4">
                                <span className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wider tw-font-medium">CSV / XLSX</span>
                                <span className="tw-text-gray-300">&middot;</span>
                                <span className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wider tw-font-medium">Max 50MB</span>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleInputChange}
                                className="tw-hidden"
                            />
                        </div>

                        {/* File Error */}
                        {fileError && (
                            <div className="tw-w-full tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3">
                                <Icon icon="mdi:alert-circle" className="tw-text-red-500 tw-text-sm tw-mt-0.5 tw-flex-shrink-0" />
                                <p className="tw-text-xs tw-text-red-600">{fileError}</p>
                            </div>
                        )}

                        {/* File Info */}
                        {file && !fileError && (
                            <div className="tw-w-full tw-mt-3 tw-flex tw-items-center tw-justify-between tw-bg-emerald-50 tw-border tw-border-emerald-200 tw-rounded-lg tw-p-3">
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <Icon icon="mdi:file-delimited-outline" className="tw-text-emerald-500 tw-text-lg" />
                                    <div>
                                        <p className="tw-text-xs tw-font-medium tw-text-gray-800">{file.name}</p>
                                        <p className="tw-text-[10px] tw-text-gray-400">{formatSize(file.size)}</p>
                                    </div>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }} className="tw-p-1 tw-rounded hover:tw-bg-emerald-100 tw-text-gray-400 hover:tw-text-red-500">
                                    <Icon icon="mdi:close" className="tw-text-sm" />
                                </button>
                            </div>
                        )}

                        {/* Download Template */}
                        <button
                            onClick={handleDownloadTemplate}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-text-blue-500 tw-text-xs tw-font-medium hover:tw-text-blue-600 tw-mt-4"
                        >
                            <Icon icon="mdi:download-outline" className="tw-text-base" />
                            Download Excel Sample
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer Buttons */}
            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-5">
                <button
                    onClick={onCancel}
                    disabled={loading}
                    className="tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-gray-500 tw-bg-gray-100 hover:tw-bg-gray-200 disabled:tw-opacity-50 tw-transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={() => onNext(file)}
                    disabled={!file || !!fileError || loading}
                    className="tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-white tw-bg-red-600 hover:tw-bg-red-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors tw-flex tw-items-center tw-gap-1.5"
                >
                    {loading && <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" />}
                    {loading ? 'Validating...' : 'Upload'}
                </button>
            </div>
        </div>
    );
};

export default BulkImportUpload;
